/**
 * createRoomModalShared — pure helpers + small types extracted from
 * CreateRoomModal.tsx for AMB-232 slice 2.
 *
 * Anything stateless and unaware of the surrounding component lives
 * here:
 *  - `PracticeLibraryItem` / `PracticeTab` (the local practice-card view-model)
 *  - VideoPractice / SoundTrack → PracticeLibraryItem mappers
 *  - Local-TZ date/time helpers (`localISODate`, `nextValidStart`, …)
 *  - Modal presets storage (`loadPresets` / `savePresets`)
 *  - `translateBackendError` (BE → RU/EN copy table for create-room
 *     validation errors); `isRu` is now injected as a parameter so this
 *     module stays free of React hooks / i18n imports.
 */
import { Platform } from 'react-native';
import { VideoPracticeItem, SoundTrack } from '../../types/auth';
import { MODERATION_BLOCK_MESSAGE, isModerationBlockMessage } from '../../services/errorMessages';
import { practiceCategoryValue } from '../../screens/live-session/create/practiceCategories';

// ── View-model types ───────────────────────────────────────────────
export interface PracticeLibraryItem {
    id: string;
    numericId: number;
    title: string;
    category: string;
    duration: number;
    /** CORE-1285 — реальная длина в секундах, когда бэкенд её знает: `duration`
     *  округлён до минут (у видео — вверх), поэтому материал короче минуты
     *  показывался как «1 мин». Подписи берут это поле, а на минуты падают,
     *  только если секунд нет. */
    durationSeconds?: number | null;
    type: 'video' | 'audio' | 'none';
    isSoundTrack?: boolean;
    playbackUrl?: string;
    playbackUrl480p?: string;
    coverImageUrl?: string | null;
    /** CORE-224 — owning master's profile id (same identity as
     *  `user.masterProfileId`), so the picker can float the current master's
     *  own practices first. `null` for soundtracks (кураторская atmosphere has
     *  no owning master). */
    masterProfileId: number | null;
}

export type PracticeTab = 'library' | 'favorites';

// ── BE shape → local view-model ────────────────────────────────────
export function mapVideoPractice(v: VideoPracticeItem): PracticeLibraryItem {
    return {
        id: String(v.id),
        numericId: v.id,
        title: v.title,
        category: practiceCategoryValue(v, v.contentType === 'practice' ? 'Practice' : 'Видео'),
        duration: v.durationMinutes || 0,
        durationSeconds: v.durationSeconds ?? null,
        type: 'video',  // All video content is treated as video practice
        playbackUrl: v.playbackUrl || undefined,
        playbackUrl480p: v.playbackUrl480p || undefined,
        coverImageUrl: v.coverImageUrl,
        masterProfileId: v.master?.id ?? null,
    };
}

export function mapSoundTrack(s: SoundTrack): PracticeLibraryItem {
    return {
        id: `audio-${s.id}`,
        numericId: s.id,
        title: s.title,
        category: s.category || 'Музыка',
        duration: Math.ceil((s.durationSeconds || 0) / 60),
        durationSeconds: s.durationSeconds ?? null,
        type: 'audio',
        isSoundTrack: true,
        playbackUrl: s.playbackUrl,
        coverImageUrl: s.coverImageUrl,
        masterProfileId: null,
    };
}

// ── Local-TZ date/time helpers ─────────────────────────────────────
// Same pattern as CreatorModals.tsx. The legacy
// `new Date().toISOString().split('T')[0]` gave us a UTC date that
// silently rolled ±1 day around local midnight, so masters in MSK / IST
// opening the modal in the evening saw «tomorrow» selected. Always
// format in local tz.
export const pad2 = (n: number): string => String(n).padStart(2, '0');
export const localISODate = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const localHHMM = (d: Date): string => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
export const todayLocalISO = (): string => localISODate(new Date());

/** Next 15-minute mark from now, in the local timezone. The previous
 *  hardcoded `09:00 + empty date` default left the date field blank in
 *  Chrome (input shows ДД.ММ.ГГГГ until the user clicks) and always
 *  pinned the time to 09:00 — useless for someone creating a room at
 *  18:30. Now masters get «today + the next quarter-hour» on open. */
export const nextValidStart = (): { date: string; time: string } => {
    const d = new Date();
    const roundedMin = Math.ceil(d.getMinutes() / 15) * 15;
    // setMinutes(60) overflows to next hour — desired behavior at xx:46-xx:59.
    d.setMinutes(roundedMin, 0, 0);
    return { date: localISODate(d), time: localHHMM(d) };
};

// Upper bound for room size; the BE validates the same range (WatchPartyService, CORE-1496).
export const MAX_ROOM_PARTICIPANTS = 1050;

// ── Modal presets storage ──────────────────────────────────────────
export const PRESETS_KEY = 'agape_room_presets';

export type CreateRoomPresets = Partial<{
    maxParticipants: number;
    isPrivate: boolean;
    disableVideoOnStart: boolean;
}>;

export function loadPresets(): CreateRoomPresets {
    if (Platform.OS !== 'web') return {};
    try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || '{}'); } catch { return {}; }
}

export function savePresets(p: CreateRoomPresets) {
    if (Platform.OS !== 'web') return;
    try { localStorage.setItem(PRESETS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ── BE error translation ───────────────────────────────────────────
/** Translate known BE error messages to RU. The BE replies in English
 *  even for ru-locale users (e.g. "ScheduledAtUtc must be in the
 *  future.") — surfacing those raw is jarring next to a Russian form.
 *  Keep the original as fallback for unknown messages.
 *
 *  Extracted as a free function for AMB-232; `isRu` is injected by the
 *  caller so this module doesn't depend on the i18n hook tree. */
export function translateBackendError(raw: string | undefined | null, isRu: boolean): string {
    if (!raw) return isRu ? 'Не удалось создать Live-сессию' : 'Failed to create room';
    const lower = raw.toLowerCase();
    // Content-moderation block (BE 422 returns the raw English BlockReason
    // "...flagged by automated moderation..."): show the branded RU copy so the
    // English never leaks next to the Russian form.
    if (isModerationBlockMessage(raw)) {
        // CORE-196 — сюда доезжает только текст сообщения, без кода, поэтому
        // отличить «нарушение» от «проверка не сработала» здесь нельзя. Для
        // второго случая бэкенд присылает русский текст сам, и он проходит
        // мимо этой ветки: isModerationBlockMessage ищет английские маркеры.
        return isRu ? MODERATION_BLOCK_MESSAGE : raw;
    }
    // Most-specific first: legacy "must be at least N minutes in the future".
    if (lower.includes('must be at least') && lower.includes('minutes in the future')) {
        return isRu
            ? 'Время начала должно быть хотя бы на несколько минут в будущем. Сдвиньте время вперёд или начните Live-сессию сейчас.'
            : 'Start time must be a few minutes in the future. Pick a later time or use a Quick Room.';
    }
    // Current BE (WatchPartyService.ValidateAsync): "ScheduledAtUtc must be in the future."
    if (lower.includes('scheduledatutc') && lower.includes('must be in the future')) {
        return isRu
            ? 'Дата и время начала должны быть в будущем. Передвиньте время вперёд.'
            : 'Start date/time must be in the future. Move it forward.';
    }
    // BE: "ScheduledAtUtc must be within 30 days."
    if (lower.includes('scheduledatutc') && lower.includes('within 30 days')) {
        return isRu
            ? 'Дату начала можно выбрать не дальше чем через 30 дней.'
            : 'Start date must be within the next 30 days.';
    }
    if (lower.includes('scheduledatutc') && lower.includes('past')) {
        return isRu ? 'Дата и время не могут быть в прошлом.' : 'Start time cannot be in the past.';
    }
    // BE: "MaxParticipants must be between 1 and 1050."
    if (lower.includes('maxparticipants') && lower.includes('between')) {
        return isRu
            ? `Число участников: от 1 до ${MAX_ROOM_PARTICIPANTS}.`
            : `Participants must be between 1 and ${MAX_ROOM_PARTICIPANTS}.`;
    }
    // BE: "CountdownDurationSeconds must be between 0 and 300."
    if (lower.includes('countdowndurationseconds') && lower.includes('between')) {
        return isRu
            ? 'Длительность обратного отсчёта: от 0 до 300 секунд.'
            : 'Countdown must be between 0 and 300 seconds.';
    }
    // BE: "Unsupported language code: {code}"
    if (lower.includes('unsupported language code')) {
        return isRu ? 'Язык не поддерживается.' : 'Unsupported language.';
    }
    if (lower.includes('soundtrackid is required')) {
        return isRu
            ? 'Для живого ведения нужен фоновый трек. Выберите его выше.'
            : 'Live facilitation needs a background track. Pick one above.';
    }
    if (lower.includes('videopracticeid')) {
        return isRu
            ? 'Не удалось подобрать практику из библиотеки. Выберите другую.'
            : 'Could not load the chosen practice. Pick another.';
    }
    if (lower.includes('too_early') || lower.includes('too early')) {
        return isRu
            ? 'Запустить можно не более чем за 5 минут до начала.'
            : 'You can only start within 5 minutes of the scheduled time.';
    }
    if (lower.includes('unauthorized') || lower.includes('forbidden')) {
        return isRu ? 'Недостаточно прав для этого действия.' : 'Not allowed.';
    }
    return raw;
}
